import { useState, type ReactNode } from 'react'
import Icon from '../docs/Icon'
import ComboBox from './ui/ComboBox'
import DatePicker from './ui/DatePicker'
import TimePicker from './ui/TimePicker'
import FileInput from './ui/FileInput'
import CharacterCount from './ui/CharacterCount'
import DataTable from './ui/DataTable'
import Modal from './ui/Modal'
import Tooltip from './ui/Tooltip'
import LanguageSelector, { type Language } from './ui/LanguageSelector'
import { allOffices, officeColumns, offices } from './examples'
import { moreSpecimens } from './specimen-groups'

// Live examples for the forms, data and feedback components. A mini specimen
// is a still preview for the catalog: no ids, nothing focusable.

type SpecimenProps = { mini?: boolean }

const states = ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming']

function Still({ children }: { children: ReactNode }) {
  return <div className="lib-framed lib-still">{children}</div>
}

function ComboBoxSpecimen({ mini }: SpecimenProps) {
  if (mini)
    return (
      <Still>
        <span className="mds-label">State</span>
        <span className="mds-input lib-fake-input">Ma</span>
        <span className="lib-fake-list">
          <span>Maine</span>
          <span className="is-active">Maryland</span>
          <span>Massachusetts</span>
        </span>
      </Still>
    )
  return <ComboBox label="State" hint="Start typing to filter the list." options={states} />
}

function DatePickerSpecimen({ mini }: SpecimenProps) {
  if (mini)
    return (
      <Still>
        <span className="mds-label">Appointment date</span>
        <span className="mds-input lib-fake-input">
          10/19/2026 <Icon name="calendar" size={16} />
        </span>
      </Still>
    )
  return <DatePicker label="Appointment date" />
}

function TimePickerSpecimen({ mini }: SpecimenProps) {
  if (mini)
    return (
      <Still>
        <span className="mds-label">Time</span>
        <span className="mds-input lib-fake-input">
          9:30 am <Icon name="clock" size={16} />
        </span>
      </Still>
    )
  return <TimePicker label="Appointment time" min={8 * 60} max={17 * 60} timeZone />
}

function MemorableDateSpecimen({ mini }: SpecimenProps) {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const id = mini ? undefined : 'spec-dob'
  return (
    <fieldset className="mds-memorable-date">
      <legend className="mds-label">Date of birth</legend>
      <div className="mds-field mds-memorable-date__month">
        <label className="mds-label" htmlFor={id && `${id}-month`}>
          Month
        </label>
        <select className="mds-select" id={id && `${id}-month`} autoComplete="bday-month" tabIndex={mini ? -1 : undefined} defaultValue="">
          <option value="">Select</option>
          {months.map((month, i) => (
            <option key={month} value={i + 1}>
              {month}
            </option>
          ))}
        </select>
      </div>
      <div className="mds-field mds-memorable-date__day">
        <label className="mds-label" htmlFor={id && `${id}-day`}>
          Day
        </label>
        <input className="mds-input" id={id && `${id}-day`} inputMode="numeric" autoComplete="bday-day" maxLength={2} tabIndex={mini ? -1 : undefined} />
      </div>
      <div className="mds-field mds-memorable-date__year">
        <label className="mds-label" htmlFor={id && `${id}-year`}>
          Year
        </label>
        <input className="mds-input" id={id && `${id}-year`} inputMode="numeric" autoComplete="bday-year" maxLength={4} tabIndex={mini ? -1 : undefined} />
      </div>
    </fieldset>
  )
}

function FileInputSpecimen({ mini }: SpecimenProps) {
  if (mini)
    return (
      <Still>
        <span className="mds-file__zone">
          <Icon name="upload" size={20} />
          Drag files here
        </span>
      </Still>
    )
  return <FileInput label="Supporting documents" hint="PDF or JPG, up to 10 MB each." accept=".pdf,.jpg,.jpeg" maxMb={10} />
}

function CharacterCountSpecimen({ mini }: SpecimenProps) {
  if (mini)
    return (
      <Still>
        <span className="mds-label">Describe the issue</span>
        <span className="mds-textarea lib-fake-input lib-fake-textarea">The office was closed during posted hours…</span>
        <span className="mds-count__message">208 characters left</span>
      </Still>
    )
  return <CharacterCount label="Describe the issue" hint="Include the office and the date." maxLength={250} />
}

function DataTableSpecimen({ mini }: SpecimenProps) {
  if (mini)
    return (
      <table className="mds-table mds-table--striped lib-mini-table">
        <thead>
          <tr>
            <th scope="col">Office</th>
            <th scope="col" className="is-numeric">
              Wait
            </th>
          </tr>
        </thead>
        <tbody>
          {offices.slice(0, 3).map((row) => (
            <tr key={row.id}>
              <td>{row.office}</td>
              <td className="is-numeric">{row.wait}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  return <DataTable caption="Field offices" rowKey="id" columns={officeColumns} rows={allOffices} filter selectable stickyHeader striped pageSize={5} />
}

function SideNavigationSpecimen({ mini }: SpecimenProps) {
  const tab = mini ? -1 : undefined
  return (
    <nav aria-label={mini ? undefined : 'Benefits'} className="lib-sidenav">
      <ul className="mds-sidenav">
        <li>
          <a className="mds-sidenav__link" href="#overview" tabIndex={tab}>
            Overview
          </a>
        </li>
        <li>
          <a className="mds-sidenav__link" href="#apply" aria-current="page" tabIndex={tab}>
            How to apply
          </a>
          <ul>
            <li>
              <a className="mds-sidenav__link" href="#online" tabIndex={tab}>
                Online
              </a>
            </li>
            <li>
              <a className="mds-sidenav__link" href="#mail" tabIndex={tab}>
                By mail
              </a>
            </li>
          </ul>
        </li>
        <li>
          <a className="mds-sidenav__link" href="#status" tabIndex={tab}>
            Check status
          </a>
        </li>
      </ul>
    </nav>
  )
}

function SummaryBoxSpecimen({ mini }: SpecimenProps) {
  return (
    <section className="mds-summary" aria-labelledby={mini ? undefined : 'spec-summary-title'}>
      {mini ? (
        <p className="mds-summary__title">Key information</p>
      ) : (
        <h2 className="mds-summary__title" id="spec-summary-title">
          Key information
        </h2>
      )}
      <ul>
        <li>Apply at least six weeks before you travel.</li>
        {!mini && <li>Bring your current passport and one photo.</li>}
      </ul>
    </section>
  )
}

function ProcessListSpecimen({ mini }: SpecimenProps) {
  const steps = [
    ['Check eligibility', 'Answer five questions to see if you qualify.'],
    ['Apply online', 'Most applications take about twenty minutes.'],
    ['Track your status', 'You will get an email at each step.'],
  ]
  return (
    <ol className="mds-process">
      {steps.slice(0, mini ? 2 : 3).map(([title, text]) => (
        <li key={title} className="mds-process__item">
          <p className="mds-process__title">{title}</p>
          {!mini && <p className="mds-muted">{text}</p>}
        </li>
      ))}
    </ol>
  )
}

function ModalSpecimen({ mini }: SpecimenProps) {
  const [open, setOpen] = useState(false)
  const [forced, setForced] = useState(false)
  if (mini)
    return (
      <Still>
        <span className="lib-fake-modal">
          <strong>Withdraw your application?</strong>
          <span className="mds-cluster">
            <span className="mds-button mds-button--primary mds-button--sm">Withdraw</span>
            <span className="mds-button mds-button--ghost mds-button--sm">Cancel</span>
          </span>
        </span>
      </Still>
    )
  const close = () => {
    setOpen(false)
    setForced(false)
  }
  return (
    <div className="mds-cluster">
      <button type="button" className="mds-button mds-button--primary" onClick={() => setOpen(true)}>
        Open modal
      </button>
      <button
        type="button"
        className="mds-button mds-button--secondary"
        onClick={() => {
          setForced(true)
          setOpen(true)
        }}
      >
        Open forced-action modal
      </button>
      <Modal
        open={open}
        onClose={close}
        forced={forced}
        title="Withdraw your application?"
        actions={
          <>
            <button type="button" className="mds-button mds-button--primary" onClick={close}>
              Withdraw
            </button>
            <button type="button" className="mds-button mds-button--ghost" onClick={close}>
              Keep my application
            </button>
          </>
        }
      >
        You can apply again later, but you will lose your place in line.
      </Modal>
    </div>
  )
}

function TooltipSpecimen({ mini }: SpecimenProps) {
  if (mini)
    return (
      <Still>
        <span className="lib-fake-tooltip">Download a PDF copy</span>
        <span className="mds-icon-button">
          <Icon name="download" />
        </span>
      </Still>
    )
  return (
    <div className="mds-cluster lib-tooltip-row">
      <Tooltip label="Download a PDF copy">
        <button type="button" className="mds-icon-button" aria-label="Download">
          <Icon name="download" />
        </button>
      </Tooltip>
      <Tooltip label="Copies the link to this page" position="bottom">
        <button type="button" className="mds-button mds-button--secondary mds-button--sm">
          <Icon name="copy" size={16} /> Copy link
        </button>
      </Tooltip>
    </div>
  )
}

function SiteAlertSpecimen({ mini }: SpecimenProps) {
  return (
    <div className="lib-stack">
      <section className="mds-site-alert" aria-label={mini ? undefined : 'Site alert'}>
        <Icon name="info" size={18} />
        <p>Online services are unavailable Sunday from 1 to 5 a.m. Eastern.</p>
      </section>
      {!mini && (
        <section className="mds-site-alert mds-site-alert--emergency" aria-label="Emergency alert">
          <Icon name="info" size={18} />
          <p>
            Offices in the Gulf Coast region are closed. <a className="mds-site-alert__link" href="#closures">
              See closures
            </a>
          </p>
        </section>
      )}
    </div>
  )
}

const languages: Language[] = [
  { lang: 'en', name: 'English', english: 'English' },
  { lang: 'es', name: 'Español', english: 'Spanish' },
  { lang: 'zh', name: '中文', english: 'Chinese' },
  { lang: 'vi', name: 'Tiếng Việt', english: 'Vietnamese' },
]

const greetings: Record<string, string> = {
  en: 'Apply for benefits',
  es: 'Solicite beneficios',
  zh: '申请福利',
  vi: 'Đăng ký phúc lợi',
}

function LanguageSelectorSpecimen({ mini }: SpecimenProps) {
  const [two, setTwo] = useState('en')
  const [many, setMany] = useState('en')
  if (mini)
    return (
      <Still>
        <span className="mds-language__toggle">
          <Icon name="globe" size={16} /> Español
        </span>
      </Still>
    )
  return (
    <div className="lib-stack">
      <div className="lib-language-demo" lang={two}>
        <LanguageSelector languages={languages.slice(0, 2)} value={two} onChange={setTwo} />
        <p className="mds-h3">{greetings[two]}</p>
      </div>
      <div className="lib-language-demo" lang={many}>
        <LanguageSelector languages={languages} value={many} onChange={setMany} />
        <p className="mds-h3">{greetings[many]}</p>
      </div>
    </div>
  )
}

const formSpecimens: Record<string, (props: SpecimenProps) => ReactNode> = {
  'combo-box': ComboBoxSpecimen,
  'date-picker': DatePickerSpecimen,
  'time-picker': TimePickerSpecimen,
  'memorable-date': MemorableDateSpecimen,
  'file-input': FileInputSpecimen,
  'character-count': CharacterCountSpecimen,
  'data-table': DataTableSpecimen,
  'side-navigation': SideNavigationSpecimen,
  'summary-box': SummaryBoxSpecimen,
  'process-list': ProcessListSpecimen,
  modal: ModalSpecimen,
  tooltip: TooltipSpecimen,
  'site-alert': SiteAlertSpecimen,
  'language-selector': LanguageSelectorSpecimen,
}

/** The live example for one of these components, or null for any other id. */
export default function FormSpecimen({ id, mini = false }: { id: string; mini?: boolean }) {
  const Render = formSpecimens[id] ?? moreSpecimens[id]
  return Render ? <Render mini={mini} /> : null
}
